import { createElement, lazy } from 'react';
import { matchPath } from 'react-router-dom';
import type { MenuProps } from 'antd';
import {
  AppstoreOutlined,
  BookOutlined,
  FileTextOutlined,
  HomeOutlined,
  ProjectOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { AppNavigationNode, AppRouteMeta } from '@/types/navigation';

const DashboardPage = lazy(() => import('@/pages/Dashboard'));
const LoginPage = lazy(() => import('@/pages/Login'));
const NotFoundPage = lazy(() => import('@/pages/NotFound'));
const ProductTypePage = lazy(() => import('@/pages/Product/Type'));
const ProductListPage = lazy(() => import('@/pages/Product/List'));
const ProjectListPage = lazy(() => import('@/pages/Project/List'));
const CustomerListPage = lazy(() => import('@/pages/Project/Customer'));
const GiftListPage = lazy(() => import('@/pages/Project/Gift'));
const OrderListPage = lazy(() => import('@/pages/Order/List'));
const RuleCenterPage = lazy(() => import('@/pages/Rule/Center'));
const RuleListPage = lazy(() => import('@/pages/Rule/List'));
const RuleViewPage = lazy(() => import('@/pages/Rule/View'));
const UserListPage = lazy(() => import('@/pages/UserCenter/User'));
const RoleListPage = lazy(() => import('@/pages/UserCenter/Role'));
const ResourceListPage = lazy(() => import('@/pages/System/Resource'));
const ApiListPage = lazy(() => import('@/pages/System/Api'));
const ProfilePage = lazy(() => import('@/pages/Profile'));

export { LoginPage, NotFoundPage };

export const appRoutes: AppNavigationNode[] = [
  {
    key: '/home',
    label: '首页',
    path: '/home',
    routePath: '',
    index: true,
    icon: 'home',
    component: DashboardPage,
    closable: false,
  },
  {
    key: '/home/product',
    label: '产品管理',
    icon: 'product',
    children: [
      {
        key: '/home/product/type',
        label: '产品类型',
        path: '/home/product/type',
        routePath: 'product/type',
        component: ProductTypePage,
      },
      {
        key: '/home/product/list',
        label: '产品列表',
        path: '/home/product/list',
        routePath: 'product/list',
        component: ProductListPage,
      },
    ],
  },
  {
    key: '/home/project',
    label: '项目管理',
    icon: 'project',
    children: [
      {
        key: '/home/project/list',
        label: '项目列表',
        path: '/home/project/list',
        routePath: 'project/list',
        component: ProjectListPage,
      },
      {
        key: '/home/project/customer',
        label: '客户管理',
        path: '/home/project/customer',
        routePath: 'project/customer',
        component: CustomerListPage,
      },
      {
        key: '/home/project/gift',
        label: '礼包清单',
        path: '/home/project/gift',
        routePath: 'project/gift',
        component: GiftListPage,
      },
    ],
  },
  {
    key: '/home/order',
    label: '订单管理',
    icon: 'order',
    children: [
      {
        key: '/home/order/list',
        label: '订单列表',
        path: '/home/order/list',
        routePath: 'order/list',
        component: OrderListPage,
      },
    ],
  },
  {
    key: '/home/rule',
    label: '内部规则',
    icon: 'rule',
    children: [
      {
        key: '/home/rule/center',
        label: '文档中心',
        path: '/home/rule/center',
        routePath: 'rule/center',
        component: RuleCenterPage,
      },
      {
        key: '/home/rule/list',
        label: '规则管理',
        path: '/home/rule/list',
        routePath: 'rule/list',
        component: RuleListPage,
      },
      {
        key: '/home/rule/view/:id',
        label: '规则详情',
        path: '/home/rule/view/:id',
        routePath: 'rule/view/:id',
        component: RuleViewPage,
        hiddenInMenu: true,
        menuHighlight: '/home/rule/list',
      },
    ],
  },
  {
    key: '/home/user-center',
    label: '用户中心',
    icon: 'user',
    children: [
      {
        key: '/home/user-center/user',
        label: '用户管理',
        path: '/home/user-center/user',
        routePath: 'user-center/user',
        component: UserListPage,
      },
      {
        key: '/home/user-center/role',
        label: '角色管理',
        path: '/home/user-center/role',
        routePath: 'user-center/role',
        component: RoleListPage,
      },
    ],
  },
  {
    key: '/home/system',
    label: '系统管理',
    icon: 'system',
    children: [
      {
        key: '/home/system/resource',
        label: '资源管理',
        path: '/home/system/resource',
        routePath: 'system/resource',
        component: ResourceListPage,
      },
      {
        key: '/home/system/api',
        label: '接口管理',
        path: '/home/system/api',
        routePath: 'system/api',
        component: ApiListPage,
      },
    ],
  },
  {
    key: '/home/profile',
    label: '个人中心',
    path: '/home/profile',
    routePath: 'profile',
    component: ProfilePage,
    hiddenInMenu: true,
  },
];

const flatEntries = (
  nodes: AppNavigationNode[],
  parentKeys: string[] = [],
): Array<{ node: AppNavigationNode; parentKeys: string[] }> =>
  nodes.flatMap((node) => {
    const current = node.path && node.component ? [{ node, parentKeys }] : [];
    const nextParents = node.children ? [...parentKeys, node.key] : parentKeys;
    const children = node.children ? flatEntries(node.children, nextParents) : [];
    return [...current, ...children];
  });

const routeEntries = flatEntries(appRoutes);

export const routeMetaList: AppRouteMeta[] = routeEntries
  .map(({ node, parentKeys }) => ({
    path: node.path!,
    label: node.label,
    closable: node.closable ?? node.path !== '/home',
    parentKeys,
    menuHighlight: node.menuHighlight,
  }))
  .sort((left, right) => right.path.length - left.path.length);

export const protectedRouteItems = routeEntries.map(({ node }) => ({
  path: node.path!,
  routePath: node.routePath,
  index: node.index,
  component: node.component!,
}));

export const findRouteMeta = (pathname: string) =>
  routeMetaList.find((route) =>
    matchPath(
      {
        path: route.path,
        end: true,
      },
      pathname,
    ),
  );

export const getSelectedMenuKey = (pathname: string) => {
  const matched = findRouteMeta(pathname);
  return matched?.menuHighlight ?? matched?.path;
};

export const getOpenKeysForPath = (pathname: string) => findRouteMeta(pathname)?.parentKeys ?? [];

const iconMap = {
  home: HomeOutlined,
  product: AppstoreOutlined,
  project: ProjectOutlined,
  order: FileTextOutlined,
  rule: BookOutlined,
  user: UserOutlined,
  system: SettingOutlined,
};

export const buildMenuItems = (nodes: AppNavigationNode[]): MenuProps['items'] =>
  nodes
    .filter((node) => !node.hiddenInMenu)
    .map((node) => ({
      key: node.path ?? node.key,
      label: node.label,
      children: node.children ? buildMenuItems(node.children) : undefined,
      icon: node.icon ? createElement(iconMap[node.icon]) : undefined,
    }));
